import React, {FC, memo, useEffect, useRef, useState} from 'react';
import {Form, FormTextInput, TextInput} from '@montana/shared/form';
import {Translate} from '@montana/shared/translate';
import {useTranslations} from '@montana/containers/translations';
import {Button, ButtonStyles} from '@montana/shared/button';
import {
    city,
    combineValidators,
    email,
    exactLength,
    fullName,
    noCityMatchZipCode,
    phone,
    required,
    streetNumber
} from '@montana/shared/form/validation';
import {IFormRef, IFormValues} from '@montana/shared/form/form.component';
import {IAddCustomerInformationRequest, ICustomerInformation, IMarket} from '@montana/models/generated/cms.interface';
import {IFieldOptionValue} from '@montana/shared/form/field/field';
import {getCityMatchingZipCode} from '@montana/services/checkout';
import {useConnectedServiceFunctions} from '@montana/shared/use-connected-service-functions';
import cx from 'clsx';
import {useMediaQuery} from 'react-responsive';
import {removeNonDigitCharacters} from '@montana/helpers/string-format/string-format.helper';

interface ICustomerInformationFormProps {
    className?: string;
    countries: IFieldOptionValue[];
    onSubmit: (form: IAddCustomerInformationRequest) => void;
    customerInformation: ICustomerInformation;
    submitForm?: boolean;
    market: IMarket | undefined;
}

export const CustomerInformationForm: FC<ICustomerInformationFormProps> = memo((props: ICustomerInformationFormProps) => {
    const fullNameField = 'fullName';
    const emailField = 'email';
    const phoneField = 'phone';
    const billingStreetField = 'billingStreet';
    const billingAdditionalAddressField = 'billingAdditionalAddress';
    const billingZipCodeField = 'billingZipCode';
    const billingCityField = 'billingCity';
    const billingCountryField = 'billingcountry';

    const {countries, onSubmit, className, customerInformation, submitForm, market} = props;

    const translate = useTranslations();

    const formRef = useRef<IFormRef>(null);

    const fieldRequiredText = translate(t => t.forms.fieldRequired, "[This field is required]");

    const [cGetCityMatchingZipCode] = useConnectedServiceFunctions(getCityMatchingZipCode);
    const [disableCity, setDisableCity] = useState<boolean>(false);
    const [noMatchingCity, setNoMatchingCity] = useState<boolean>(false);
    const [showZipCodeLabel, setShowZipCodeLabel] = useState<boolean>(true);
    const isMobile = useMediaQuery({maxWidth: 440});

    useEffect(() => {
        if (isMobile && !!customerInformation.billingAddress?.zipCode) {
            setShowZipCodeLabel(false);
        }
    }, [])

    function handleMatchZipCodeToCity(zipCode: string) {

        if (!market?.marketId || market?.checkoutZipCodeLength !== zipCode.length) {
            formRef?.current?.getForm()?.setFieldValue(billingCityField, '');
            setDisableCity(false);
            setNoMatchingCity(true);
            formRef?.current?.getForm()?.runSingleFieldLevelValidation(billingZipCodeField, zipCode);
            formRef?.current?.getForm()?.setFieldTouched(billingZipCodeField, true, true);
            return false;
        }

        cGetCityMatchingZipCode(zipCode, market?.marketId)
            .then(response => {
                if (response.ok) {
                    response.json().then(city => {
                        formRef?.current?.getForm()?.setFieldValue(billingCityField, city);
                        setDisableCity(true);
                        setNoMatchingCity(false);
                        formRef?.current?.getForm()?.runSingleFieldLevelValidation(billingZipCodeField, zipCode);
                        formRef?.current?.getForm()?.setFieldTouched(billingZipCodeField, true, true);
                    })
                } else {
                    response.json().then(() => {
                        setDisableCity(false);
                        setNoMatchingCity(true);
                        formRef?.current?.getForm()?.runSingleFieldLevelValidation(billingZipCodeField, zipCode);
                        formRef?.current?.getForm()?.setFieldTouched(billingZipCodeField, true, true);
                    })
                }
            })
            .catch(e => console.log('error', e))
    }

    useEffect(() => {
        if (submitForm) {
            formRef.current?.getForm()?.submitForm();
        }
    }, [submitForm]);

    function handleSubmit(form: IFormValues) {
        onSubmit({
            personalInformation: {
                fullName: form[fullNameField],
                email: form[emailField],
                phone: removeNonDigitCharacters(form[phoneField]),
            },
            billingAddress: {
                countryCode: form[billingCountryField],
                street: form[billingStreetField],
                additionalAddress: form[billingAdditionalAddressField],
                zipCode: form[billingZipCodeField],
                city: form[billingCityField],
                country: countries.find(c => c.value === form[billingCountryField])?.text || '',
            },
            shippingAddress: customerInformation.shippingAddress,
            billingAddressSameAsShipping: customerInformation.billingAddressSameAsShipping,
        })
    }

    return (
        <section className={`checkout-customer-information${className ? ` ${className}` : ''}`}>
            <h5 className="checkout-b2c-base__heading">
                <Translate value="checkoutPage.personalInformationHeading" defaultValue="[Personal Information]"/>
            </h5>
            <Form
                onSubmit={handleSubmit}
                initialValues={{}}
                ref={formRef}
            >
                {() => (
                    <>
                        <div>
                            <h6 className="checkout-b2c-base__sub-heading">
                                <Translate value="checkoutPage.personalInformationHeading"
                                           defaultValue="[Personal Information]"/>
                            </h6>
                            <FormTextInput
                                name={fullNameField}
                                label={translate(t => t.checkoutPage.personalInformationFullName, '[Full name:]')}
                                validate={combineValidators(
                                    required(fieldRequiredText),
                                    fullName(translate(t => t.forms.invalidFullName, '[Please write the full name]'))
                                )}
                                required
                                defaultValue={customerInformation.personalInformation?.fullName ?? ''}
                            />
                            <FormTextInput
                                name={emailField}
                                label={translate(t => t.checkoutPage.personalInformationEmail, '[E-mail:]')}
                                validate={combineValidators(
                                    required(fieldRequiredText),
                                    email(translate(t => t.forms.invalidEmail, '[Email is not valid]'))
                                )}
                                required
                                defaultValue={customerInformation.personalInformation?.email ?? ''}
                            />
                            <div className="checkout-customer-information__columns form-column-size-1">
                                <TextInput name="countryCode" value={market?.phonePrefixList?.[0]} disabled/>
                                <div className="checkout-customer-information__phone">
                                    <FormTextInput
                                        name={phoneField}
                                        label={translate(t => t.checkoutPage.personalInformationPhone, '[Phone:]')}
                                        validate={combineValidators(
                                            required(fieldRequiredText),
                                            phone(market?.marketId ?? '', translate(t => t.forms.invalidPhone, '[Phone number is not correct]')),
                                        )}
                                        required
                                        defaultValue={customerInformation.personalInformation?.phone ?? ''}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <h6 className="checkout-b2c-base__sub-heading checkout-customer-information__address-heading">
                                <Translate value="checkoutPage.personalInformationAddressHeading" defaultValue="[Address]"/>
                            </h6>
                            <FormTextInput
                                name={billingStreetField}
                                label={translate(t => t.checkoutPage.personalInformationStreet, '[Street and number:]')}
                                validate={
                                    combineValidators(
                                        required(fieldRequiredText),
                                        streetNumber(translate(t => t.forms.invalidAddress, '[Street and number is not correct]'))
                                    )}
                                required
                                defaultValue={customerInformation.billingAddress?.street ?? ''}
                            />
                            <FormTextInput
                                name={billingAdditionalAddressField}
                                label={translate(t => t.checkoutPage.personalInformationAdditionalAddress, '[Additional address:]')}
                                required={false}
                                defaultValue={customerInformation.billingAddress?.additionalAddress ?? ''}
                            />
                            <div
                                className={cx('checkout-customer-information__columns', market?.checkoutZipCodeLength ? `form-column-size-${market?.checkoutZipCodeLength}` : '')}>
                                <div className="checkout-customer-information__zipCode">
                                    <TextInput
                                        name="zipCode"
                                        label={!isMobile || showZipCodeLabel ? translate(t => t.checkoutPage.personalInformationZipCode, '[Zip code:]') : ''}
                                        onChange={e => {
                                            const zipCodeClean = removeNonDigitCharacters(e.target.value);
                                            handleMatchZipCodeToCity(zipCodeClean);
                                            formRef?.current?.getForm()?.setFieldValue(billingZipCodeField, zipCodeClean);
                                            if (isMobile && zipCodeClean.length > 0) {
                                                setShowZipCodeLabel(false);
                                            } else {
                                                setShowZipCodeLabel(true);
                                            }
                                        }
                                        }
                                        onBlur={(e: any) => {
                                            const zipCodeClean = removeNonDigitCharacters(e.target.value);
                                            if (isMobile && !zipCodeClean.length) {
                                                setShowZipCodeLabel(true);
                                            }
                                        }}
                                        required
                                        defaultValue={customerInformation.billingAddress?.zipCode ?? ''}
                                    />
                                    <FormTextInput
                                        name={billingZipCodeField}
                                        defaultValue={customerInformation.billingAddress?.zipCode}
                                        required
                                        hidden
                                        validate={
                                            combineValidators(
                                                required(fieldRequiredText),
                                                exactLength(market?.checkoutZipCodeLength ?? 6, translate(t => t.forms.invalidZipCodeLength, '[Invalid zip code length]')),
                                                noCityMatchZipCode(noMatchingCity, translate(t => t.forms.invalidNoCityMatchedZipCode, '[Didn\'t find any matching city]'))
                                            )}
                                    />
                                </div>
                                <div className="checkout-customer-information__city">
                                    <FormTextInput
                                        name={billingCityField}
                                        label={translate(t => t.checkoutPage.personalInformationCity, '[City:]')}
                                        validate={
                                            combineValidators(
                                                required(fieldRequiredText),
                                                city(translate(t => t.forms.invalidCity, '[City is not correct]'))
                                            )
                                        }
                                        disabled={disableCity || !!customerInformation.billingAddress?.city.length}
                                        required
                                        defaultValue={customerInformation.billingAddress?.city ?? ''}
                                    />
                                </div>
                            </div>
                            {!!countries?.length && (
                                <div className="checkout-customer-information__country">
                                    <FormTextInput
                                        name={billingCountryField}
                                        defaultValue={customerInformation.billingAddress?.countryCode ?? countries[0].value}
                                        hidden
                                    />
                                    <FormTextInput
                                        name="billingCountryName"
                                        defaultValue={countries[0].text}
                                        disabled
                                    />
                                    {/*<FormSelect
                                        name={billingCountryField}
                                        options={countries}
                                        placeholder={translate(t => t.checkoutPage.personalInformationCountry, '[Select country]')}
                                        hiddenLabel
                                        validate={required(fieldRequiredText)}
                                        defaultValue={customerInformation.billingAddress?.countryCode ?? countries[0]?.value}
                                    />*/}
                                    {/*<FormSelect
                                        name={billingCountryField}
                                        options={countries}
                                        placeholder={translate(t => t.checkoutPage.personalInformationCountry, '[Select country]')}
                                        hiddenLabel
                                        validate={required(fieldRequiredText)}
                                        defaultValue={customerInformation.billingAddress?.countryCode ?? countries.find(c => c.value === 'DNK')?.value ?? countries[0]?.value}
                                        inline={true}
                                    />*/}
                                </div>
                            )}
                        </div>

                        <Button
                            className="checkout-b2c-base__submit-button"
                            style={ButtonStyles.Tertiary}
                            submitButton
                        >
                            <Translate
                                value="checkoutPage.personalInformationProceedToShipping"
                                defaultValue="[Proceed to Shipping]"
                            />
                        </Button>
                    </>
                )}
            </Form>
        </section>
    )
});
